import { Localized } from "@fluent/react";
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { SaveButton } from "../../../../app/components/SaveButton";
import { CancelButton } from "../../../../app/components/CancelButton";
import { AppId } from "../../../../app/appTypes";
import { useAppDispatch } from "../../../../app/hooks";
import { changeDocumentTeamMemberDeadline } from "../teamMemberApi";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";

export interface TeamMemberChangeDeadlineDialogProps {
    documentId: AppId;
    onCancel: () => void;
    onSuccessfulSave: (deadline: string) => void;
}

const getDefaultDeadline = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);

    return date.toISOString().split("T")[0];
};

export const TeamMemberChangeDeadlineDialog = ({
    documentId,
    onCancel,
    onSuccessfulSave,
}: TeamMemberChangeDeadlineDialogProps) => {
    const dispatch = useAppDispatch();
    const [deadline, setDeadline] = useState(getDefaultDeadline);
    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")


    const deadlineError = useMemo(() => deadline.trim() === "", [deadline]);

    const handleSaveClick = async () => {
        if (deadlineError) {
            return;
        }

        setSaving(true);
        setServerError("");
        try {
            const result = await dispatch(changeDocumentTeamMemberDeadline({
                documentId,
                deadline,
                teamMemberId: null,
                forAllMembers: true,
            }));

            if (changeDocumentTeamMemberDeadline.fulfilled.match(result)) {
                dispatch(showSuccess("saved"));
                onSuccessfulSave(deadline);
            } else {
                let errorMsg = "error"
                if (result.payload && result.payload.kind === 'http') {
                    const problem = result.payload.problem
                    if (problem) {
                        errorMsg = problem.title

                    }
                }
                setServerError(errorMsg)
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="xs" >
            <DialogTitle>
                <Localized id="document-change-deadline">
                    <span>_Zmień deadline</span>
                </Localized>
            </DialogTitle>

            <DialogContent>
                <Alert severity="info" variant="outlined" sx={{ mt: 2, mb: 2 }}>
                    <Localized id="document-change-deadline-description">
                        <span>_Ustaw nowy termin deadline dla członków zespołu.</span>
                    </Localized>
                </Alert>

                <TextField
                    fullWidth
                    required
                    type="date"
                    value={deadline}
                    onChange={(event) => setDeadline(event.target.value)}
                    error={deadlineError}
                    helperText={
                        deadlineError ? (
                            <Localized id="field-is-required">
                                <span>_Pole jest wymagane</span>
                            </Localized>
                        ) : (
                            " "
                        )
                    }
                    label={
                        <Localized id="urs-action-deadline">
                            <span>_Termin</span>
                        </Localized>
                    }
                    InputLabelProps={{ shrink: true }}
                />
                {
                    serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                        <Localized id={serverError}>
                            <span>_Server error</span>
                        </Localized>
                    </Alert>
                }
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <SaveButton loading={saving} onClick={handleSaveClick} disabled={deadlineError} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
